import React from "react";
import { castDraft, current } from "immer";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { PlayerStoreApi } from "@common/player/state/player-state";

export interface StackScreenOptions {
  title?: string;
  stackedHeaderRight?: React.ReactNode;
  stackedBodyPadding?: boolean;
  className?: string;
  isDismissable?: boolean;
  returnFocusToTrigger?: boolean;
  renderInOutlet?: boolean;
  playerStore?: PlayerStoreApi | null;
  routeContext?: unknown;
  locationContext?: unknown;
}

export type StackScreenContent =
  | React.ReactNode
  | ((ctx: unknown) => React.ReactNode);

export interface StackScreenInput extends StackScreenOptions {
  content: StackScreenContent;
  onClosed?: (value?: any) => void;
  triggerElement?: HTMLElement | null;
}

export interface StackScreenEntry extends StackScreenInput {
  key: string;
  requestedClose: boolean;
  closeValue?: any;
}

interface StackNavigatorState {
  entries: StackScreenEntry[];
  push: (entry: StackScreenInput) => string;
  update: (key: string, entry: Partial<StackScreenInput>) => void;
  requestClose: (key: string, value?: any) => void;
  completeClose: (key: string, value?: any) => void;
  closeTop: (value?: any) => void;
  dismissAll: (value?: any) => void;
}

let stackScreenSequence = 0;

export function logStackNavigator(event: string, payload?: unknown) {
  void event;
  void payload;
}

function createStackScreenKey() {
  stackScreenSequence += 1;
  return `stack-screen-${stackScreenSequence}`;
}

export const useStackNavigatorStore = create<StackNavigatorState>()(
  immer((set, get) => ({
    entries: [],
    push: entry => {
      const key = createStackScreenKey();
      set(state => {
        state.entries.push(
          castDraft({
            ...entry,
            key,
            requestedClose: false,
            closeValue: undefined,
          }),
        );
      });
      logStackNavigator("store.push", {
        key,
        title: entry.title,
        entries: get().entries.map(item => item.key),
      });
      return key;
    },
    update: (key, entry) => {
      set(state => {
        const existingEntry = state.entries.find(item => item.key === key);
        if (!existingEntry) {
          return;
        }

        Object.assign(existingEntry, entry);
      });
      logStackNavigator("store.update", {
        key,
        title: entry.title,
      });
    },
    requestClose: (key, value) => {
      set(state => {
        const existingEntry = state.entries.find(item => item.key === key);
        if (!existingEntry || existingEntry.requestedClose) {
          return;
        }

        existingEntry.requestedClose = true;
        existingEntry.closeValue = value;
      });
      logStackNavigator("store.requestClose", {key, value});
    },
    completeClose: (key, value) => {
      let removedEntry: StackScreenEntry | undefined;

      set(state => {
        const index = state.entries.findIndex(item => item.key === key);
        if (index === -1) {
          return;
        }

        removedEntry = current(state.entries[index]) as StackScreenEntry;
        state.entries.splice(index, 1);
      });

      const finalValue = value ?? removedEntry?.closeValue;
      logStackNavigator("store.completeClose", {
        key,
        finalValue,
        removed: removedEntry?.title,
        entries: get().entries.map(item => item.key),
      });
      removedEntry?.onClosed?.(finalValue);

      if (
        removedEntry?.returnFocusToTrigger !== false &&
        removedEntry?.triggerElement?.isConnected
      ) {
        removedEntry.triggerElement.focus();
      }
    },
    closeTop: value => {
      const topEntry = get().entries.at(-1);
      logStackNavigator("store.closeTop", {
        key: topEntry?.key,
        value,
      });
      if (!topEntry) {
        return;
      }

      get().requestClose(topEntry.key, value);
    },
    dismissAll: value => {
      const keys = get().entries.map(entry => entry.key);
      logStackNavigator("store.dismissAll", {keys, value});
      keys.forEach(key => {
        get().requestClose(key, value);
      });
    },
  }))
);

export const stackNavigatorState = useStackNavigatorStore.getState();

export function useStackNavigator() {
  return {
    push: stackNavigatorState.push,
    update: stackNavigatorState.update,
    requestClose: stackNavigatorState.requestClose,
    completeClose: stackNavigatorState.completeClose,
    closeTop: stackNavigatorState.closeTop,
    dismissAll: stackNavigatorState.dismissAll,
  };
}
